import { FC, useCallback, useState, useRef, useEffect } from 'react';
import {
  Block,
  Button,
  Form,
  FormItem,
  Header,
  InfoBlock,
  Input,
  ScrollView,
} from '$uikit';
import { Loader } from '$shared/components';
import { formatInputAmount } from '$utils/number';
import { CurrencyEnum } from '$store/models';
import { useModalClose, useTranslator, useCurrentUserInfo } from '$hooks';
import { confirmSheet, formatCurrency } from 'utils';
import { BundleAttach } from '$core/CreateBundle/BundleAttach/BundleAttach';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '$store/rootReducer';
import { attachPhotosSelector, attachItemsSelector, attachActions } from '$store/attach';
import { toastActions } from '$store/toast';
import { goBack } from '$navigation/helper';
import {
  adventCalendarActions,
  adventCalendarSelector,
  editingBundleSelector,
  loadingBundleIdSelector,
} from '$store/adventCalendar';
import './CreateAdventCalendarBundle.sass';

interface CreateAdventCalendarBundleProps {
  day?: number;
  bundleId?: number;
}

export const CreateAdventCalendarBundle: FC<CreateAdventCalendarBundleProps> = ({
  day = 1,
  bundleId,
}) => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const currentUser = useCurrentUserInfo();

  const adventCalendar = useSelector((state: RootState) =>
    adventCalendarSelector(state, currentUser?.id!),
  );

  const editingBundle = useSelector(editingBundleSelector);
  const loadingBundleId = useSelector(loadingBundleIdSelector);

  const isLoading = !editingBundle && bundleId ? loadingBundleId === bundleId : false;

  const [form, setForm] = useState({
    day,
    price: editingBundle?.price.toString() || '',
  });

  const decryptKey = editingBundle?.decryptKey;
  const initialPriceRef = useRef<string>(editingBundle?.price.toString() || '');

  const mediaPreviewAttachments = useSelector((state: RootState) =>
    attachPhotosSelector(state, 'advent_bundle_previews'),
  );
  const mediaContentAttachments = useSelector((state: RootState) =>
    attachPhotosSelector(state, 'advent_bundle_content'),
  );

  const previews = useSelector((state: RootState) =>
    attachItemsSelector(state, 'advent_bundle_previews'),
  );
  const content = useSelector((state: RootState) =>
    attachItemsSelector(state, 'advent_bundle_content'),
  );

  useEffect(() => {
    if (bundleId && (!editingBundle || editingBundle.bundleId !== bundleId)) {
      dispatch(adventCalendarActions.loadBundleForEditing({ bundleId }));
    }
  }, [bundleId, editingBundle, dispatch]);

  useEffect(() => {
    if (editingBundle && bundleId && editingBundle.bundleId === bundleId) {
      const priceStr = editingBundle.price.toString();
      setForm((prev) => ({ ...prev, price: priceStr }));
      initialPriceRef.current = priceStr;
    }
  }, [editingBundle, bundleId]);

  const handleClose = () => {
    const hasChanges = bundleId
      ? form.price !== initialPriceRef.current
      : form.price || mediaPreviewAttachments.length || mediaContentAttachments.length;

    const cleanup = () => {
      dispatch(attachActions.clear('advent_bundle_previews'));
      dispatch(attachActions.clear('advent_bundle_content'));
      // dispatch(adventCalendarActions.clearEditingBundle());
    };

    if (hasChanges) {
      confirmSheet(t('form_reset_confirm')).then((value) => {
        if (value) {
          cleanup();
          goBack();
        }
      });
      return false;
    } else {
      cleanup();
      goBack();
      return false;
    }
  };

  const modalName = bundleId
    ? `edit_advent_bundle-${day}-${bundleId}`
    : `create_advent_bundle-${day}`;

  useModalClose(modalName, () => {
    return handleClose();
  });

  const handleSave = useCallback(() => {
    const price = form.price ? parseFloat(form.price) : 0;

    if (!mediaPreviewAttachments.length) {
      return dispatch(
        toastActions.setToastFail(t('bundles_create_toast_preview_required')),
      );
    } else if (!mediaContentAttachments.length) {
      return dispatch(
        toastActions.setToastFail(t('bundles_create_toast_content_required')),
      );
    }

    if (!adventCalendar) {
      return dispatch(toastActions.setToastFail(t('advent_calendar_not_found')));
    }

    if ([previews, content].includes('photo_uploading')) {
      return dispatch(toastActions.setToastFail(t('photo_uploading_err')));
    }

    if (typeof previews === 'string' || typeof content === 'string') {
      return dispatch(toastActions.setToastFail(t('photo_uploading_err')));
    }

    const bundleName = t('advent_header_february_14');
    const bundleDescription = t('advent_day', { day });

    if (bundleId) {
      dispatch(
        adventCalendarActions.updateAdventBundle({
          bundleId,
          name: bundleName,
          description: bundleDescription,
          price,
          previews,
          content,
        }),
      );
    } else {
      dispatch(
        adventCalendarActions.createAdventBundle({
          calendarId: adventCalendar.id,
          day,
          name: bundleName,
          description: bundleDescription,
          price,
          previews,
          content,
        }),
      );
    }
  }, [
    form,
    mediaPreviewAttachments,
    mediaContentAttachments,
    adventCalendar,
    previews,
    content,
    day,
    t,
    dispatch,
  ]);

  const formPrice = Math.max(parseFloat(form.price || '0'), 0);

  if (isLoading) {
    return (
      <>
        <Header showBackButton backButtonOnClick={handleClose}>
          {t('advent_day', { day })}
        </Header>
        <Loader isWrapped />
      </>
    );
  }

  return (
    <>
      <Header showBackButton backButtonOnClick={handleClose}>
        {t('advent_day', { day })}
      </Header>

      <ScrollView
        footer={
          <Button onClick={handleSave}>
            {bundleId ? t('bundles_save_button') : t('bundles_create_button')}
          </Button>
        }
      >
        <Form>
          <FormItem>
            <Block>
              <InfoBlock type="info">{t('advent_bundle_info')}</InfoBlock>
            </Block>
          </FormItem>

          <FormItem
            title={t('bundles_form_price_title')}
            description={
              formPrice > 0
                ? t('bundles_form_price_desc', {
                    amount: formatCurrency(formPrice * 0.8, CurrencyEnum.EUR),
                  })
                : t('advent_bundle_price_free_description')
            }
          >
            <Input
              type="price"
              onChange={(e) =>
                setForm({ ...form, price: formatInputAmount(e.target.value) })
              }
              currency={CurrencyEnum.EUR}
              placeholder={t('advent_bundle_price_placeholder')}
              value={form.price}
              minPrice={0}
              maxPrice={200}
            />
          </FormItem>

          <FormItem title={t('bundles_form_preview_title')}>
            <BundleAttach
              pickerId="advent_bundle_previews"
              limit={1}
              decryptKey={decryptKey}
            />
          </FormItem>

          <FormItem title={t('bundles_form_content_title')}>
            <BundleAttach
              pickerId="advent_bundle_content"
              limit={50}
              decryptKey={decryptKey}
            />
          </FormItem>
        </Form>
      </ScrollView>
    </>
  );
};
